import { useState, useCallback } from "react";

interface UseLocationInteractionsProps {
  canOpenMoveBilletsModal: boolean;
}

interface UseLocationInteractionsReturn {
  // Location selection state
  sourceLocationId: number | null;
  destinationLocationId: number | null;
  selectedLocationIdForClear: number | null;

  // Stable interaction handlers (useCallback wrapped)
  handleLocationClick: (
    locationId: number,
    onOpenMoveModal: () => void,
  ) => void;
  handleLocationPick: (
    locationId: number,
    onSetSelectedLocationForEdition: (id: number) => void,
    onOpenLocationContentEditionModal: () => void,
  ) => void;
  handleLocationHoldTap: (
    locationId: number,
    onOpenClearModal: () => void,
  ) => void;
  clearAllSelections: () => void;
  clearLocationForClear: () => void;

  // Computed state helpers
  hasLocationSelections: boolean;
  canClearSelections: boolean;
}

export const useLocationInteractions = ({
  canOpenMoveBilletsModal,
}: UseLocationInteractionsProps): UseLocationInteractionsReturn => {
  // Location selection state
  const [sourceLocationId, setSourceLocationId] = useState<number | null>(null);
  const [destinationLocationId, setDestinationLocationId] = useState<
    number | null
  >(null);
  const [selectedLocationIdForClear, setSelectedLocationIdForClear] = useState<
    number | null
  >(null);

  // Clear all location selections
  const clearAllSelections = useCallback(() => {
    setSourceLocationId(null);
    setDestinationLocationId(null);
  }, []);

  // Clear location for clear
  const clearLocationForClear = useCallback(() => {
    setSelectedLocationIdForClear(null);
  }, []);

  // Handle location clicks for move billets functionality
  const handleLocationClick = useCallback(
    (locationId: number, onOpenMoveModal: () => void) => {
      // Early return if user cannot open move billets modal
      if (!canOpenMoveBilletsModal) {
        return;
      }

      // Case 1: No source selected yet
      if (sourceLocationId === null) {
        setSourceLocationId(locationId);
        return;
      }

      // Case 2: Clicking same source again - deselect source
      if (sourceLocationId === locationId && destinationLocationId === null) {
        setSourceLocationId(null);
        return;
      }

      // Case 3: Clicking source again when destination is set - reset everything
      if (sourceLocationId === locationId && destinationLocationId !== null) {
        setSourceLocationId(null);
        setDestinationLocationId(null);
        // Modal will be closed automatically by clearAllSelections in the modal hook
        return;
      }

      // Case 4: Clicking same destination again - keep it (no change) and open modal
      if (destinationLocationId === locationId) {
        if (canOpenMoveBilletsModal) {
          onOpenMoveModal();
        }
        return; // keep current selection
      }

      // Case 5: Clicking different location when destination is set - change destination
      if (destinationLocationId !== null) {
        setDestinationLocationId(locationId);
        if (canOpenMoveBilletsModal) {
          onOpenMoveModal();
        }
        return;
      }

      // Case 6: First destination selection
      if (destinationLocationId === null) {
        setDestinationLocationId(locationId);
        // Check permissions before opening modal
        if (canOpenMoveBilletsModal) {
          onOpenMoveModal();
        }
        return;
      }
    },
    [sourceLocationId, destinationLocationId, canOpenMoveBilletsModal],
  );

  // Handle location selection for content edition (from LocationSelectionModal)
  const handleLocationPick = useCallback(
    (
      locationId: number,
      onSetSelectedLocationForEdition: (id: number) => void,
      onOpenLocationContentEditionModal: () => void,
    ) => {
      onSetSelectedLocationForEdition(locationId);
      onOpenLocationContentEditionModal();
    },
    [],
  );

  // Handle location hold tap for clear functionality - opens modal directly
  const handleLocationHoldTap = useCallback(
    (locationId: number, onOpenClearModal: () => void) => {
      setSelectedLocationIdForClear(locationId);
      onOpenClearModal();
    },
    [],
  );

  // Computed state helpers
  const hasLocationSelections =
    sourceLocationId !== null || destinationLocationId !== null;
  const canClearSelections = hasLocationSelections;

  return {
    // Location selection state
    sourceLocationId,
    destinationLocationId,
    selectedLocationIdForClear,

    // Stable interaction handlers
    handleLocationClick,
    handleLocationPick,
    handleLocationHoldTap,
    clearAllSelections,
    clearLocationForClear,

    // Computed state helpers
    hasLocationSelections,
    canClearSelections,
  };
};
